'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { usePieChartStyles } from './usePieChartStyles';
ChartJS.register(ArcElement, Tooltip, Legend);
const PieChart = ({ title, centerText, data, borderWidth = 2, showLegend = true, fillParent, height }) => {
    const styles = usePieChartStyles();
    const chartRef = useRef(null);
    const [legendItems, setLegendItems] = useState([]);
    const chartData = {
        labels: data.labels,
        datasets: [
            {
                data: data.values,
                backgroundColor: data.colors,
                borderColor: 'transparent',
                borderWidth: borderWidth,
                hoverBorderWidth: 3
            }
        ]
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false // Disable default legend
            },
            tooltip: {
                enabled: true,
                backgroundColor: '#454F5B',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                cornerRadius: 10,
                padding: 10,
                displayColors: false
            },
            datalabels: {
                display: false,
                color: '#fff',
                font: {
                    weight: 400,
                    size: 12,
                    family: 'ARSMaquettePro'
                }
                // formatter: (value: number, context: any) => {
                //   const dataset = context.chart.data.datasets[0];
                //   const total = dataset.data.reduce((sum: number, val: number) => sum + val, 0);
                //   const percentage = Math.round((value / total) * 100);
                //   return percentage + '%';
                // },
            }
        }
    };
    useEffect(() => {
        if (chartRef.current && showLegend) {
            const chart = chartRef.current;
            const items = chart.legend?.legendItems || [];
            setLegendItems(items);
        }
    }, [showLegend, data]);
    const handleLegendClick = (index) => {
        if (chartRef.current) {
            const chart = chartRef.current;
            chart.toggleDataVisibility(index);
            chart.update();
            // Update legend items to reflect visibility changes
            const items = chart.legend?.legendItems || [];
            setLegendItems([...items]);
        }
    };
    return (_jsxs(Box, { sx: styles.container, children: [title && (_jsx(Box, { mb: 2.6, display: 'flex', justifyContent: 'center', alignItems: 'center', children: _jsx(Typography, { variant: 'h6', sx: styles.title, children: title }) })), _jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: fillParent ? '100%' : (height ?? 200),
                    minHeight: 0
                }, children: [_jsx(Box, { sx: { flex: 1, minHeight: 0, position: 'relative' }, children: _jsx(Pie, { ref: chartRef, data: chartData, options: options }) }), showLegend && legendItems.length > 0 && (_jsx(Box, { sx: {
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            pb: 1,
                            mt: 1
                        }, children: _jsx(Box, { sx: {
                                display: 'flex',
                                gap: 2,
                                minWidth: 'max-content',
                                px: 1,
                                justifyContent: 'center'
                            }, children: legendItems.map((item, index) => (_jsxs(Box, { onClick: () => handleLegendClick(index), sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 0,
                                    cursor: 'pointer',
                                    opacity: item.hidden ? 0.4 : 1,
                                    transition: 'opacity 0.2s',
                                    '&:hover': {
                                        opacity: item.hidden ? 0.6 : 0.8
                                    }
                                }, children: [_jsx(Box, { sx: {
                                            width: 16,
                                            height: 16,
                                            backgroundColor: item.fillStyle,
                                            borderRadius: '50%',
                                            flexShrink: 0
                                        } }), _jsx(Typography, { sx: {
                                            whiteSpace: 'nowrap',
                                            fontSize: '10px',
                                            color: '#161C24',
                                            textDecoration: item.hidden ? 'line-through' : 'none'
                                        }, children: item.text })] }, index))) }) }))] })] }));
};
export default PieChart;
